import React, { useRef, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Swal from 'sweetalert2';
import { displayError } from '../../helpers';
import {
    deletePurchaseInvoiceDocument,
    uploadPurchaseInvoiceDocument,
} from '../../services/PurchaseInvoiceServices';
import {
    GRN_DOCUMENT_ACCEPT,
    GRN_DOCUMENT_MAX_BYTES,
    GRN_DOCUMENT_TYPES,
    createPendingGrnDocument,
    getGrnDocumentTypeLabel,
    mapSavedGrnDocument,
    openGrnDocument,
    revokePendingGrnDocument,
} from '../store/grnDocumentUtils';
import '../store/GrnUploadCentreModal.css';

function PiUploadCentreModal({
    isOpen,
    toggle,
    cookies,
    invoiceId,
    documents,
    onDocumentsChange,
}) {
    const fileInputRef = useRef(null);
    const [docType, setDocType] = useState('invoice');
    const [busy, setBusy] = useState(false);
    const savedId = invoiceId ? parseInt(invoiceId, 10) : null;
    const canUploadNow = Number.isInteger(savedId) && savedId > 0;

    const addDocument = async (file) => {
        if (!file) {
            return;
        }
        if (file.size > GRN_DOCUMENT_MAX_BYTES) {
            Swal.fire('File size must be 15 MB or less.', '', 'info');
            return;
        }
        if (canUploadNow) {
            setBusy(true);
            try {
                const saved = await uploadPurchaseInvoiceDocument(cookies, savedId, docType, file);
                onDocumentsChange([...(documents || []), mapSavedGrnDocument(saved)]);
            } catch (error) {
                displayError(error.response && error.response.data, 'Upload Failed');
            } finally {
                setBusy(false);
            }
            return;
        }
        onDocumentsChange([...(documents || []), createPendingGrnDocument(docType, file)]);
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        addDocument(file);
    };

    const handleRemove = async (doc) => {
        const confirmed = await Swal.fire({
            title: 'Remove this document?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        });
        if (!confirmed.isConfirmed) {
            return;
        }
        if (doc.isPending) {
            revokePendingGrnDocument(doc);
            onDocumentsChange((documents || []).filter((item) => item.id !== doc.id));
            return;
        }
        if (!canUploadNow) {
            return;
        }
        setBusy(true);
        try {
            await deletePurchaseInvoiceDocument(cookies, savedId, doc.id);
            onDocumentsChange((documents || []).filter((item) => item.id !== doc.id));
        } catch (error) {
            displayError(error.response && error.response.data, 'Delete Failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg" backdrop="static">
            <ModalHeader toggle={toggle}>Upload Centre</ModalHeader>
            <ModalBody>
                <p className="grn-upload-centre-hint mb-2">
                    Upload Invoice, Delivery Challan, E-Way Bill, Weighment Slip and other documents for this purchase invoice.
                    {canUploadNow
                        ? ' Files are saved immediately.'
                        : ' Files selected here are attached when you save the invoice.'}
                </p>
                <div className="w-100 d-flex flex-wrap align-items-center grn-upload-centre-row mb-2">
                    <label htmlFor="pi_doc_type" className="form-group col-sm-4 text-right mb-0">
                        Document Type
                    </label>
                    <select
                        id="pi_doc_type"
                        className="form-control col-sm-7"
                        value={docType}
                        onChange={(event) => setDocType(event.target.value)}
                        disabled={busy}
                    >
                        {GRN_DOCUMENT_TYPES.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                    </select>
                </div>
                <div className="w-100 d-flex flex-wrap align-items-center grn-upload-centre-row mb-2">
                    <label htmlFor="pi_doc_file" className="form-group col-sm-4 text-right mb-0">
                        Choose File
                    </label>
                    <div className="col-sm-7 px-0">
                        <input
                            ref={fileInputRef}
                            id="pi_doc_file"
                            type="file"
                            className="form-control"
                            accept={GRN_DOCUMENT_ACCEPT}
                            disabled={busy}
                            onChange={handleFileChange}
                        />
                    </div>
                </div>
                <div className="grn-upload-centre-list">
                    <div className="grn-upload-centre-list-header">
                        <span>Type</span>
                        <span>File</span>
                        <span>Action</span>
                    </div>
                    {(documents || []).length === 0 && (
                        <div className="grn-upload-centre-empty">No documents attached.</div>
                    )}
                    {(documents || []).map((doc) => (
                        <div key={doc.id} className="grn-upload-centre-list-row">
                            <span>{getGrnDocumentTypeLabel(doc.doc_type)}</span>
                            <span className="grn-upload-centre-filename" title={doc.original_name}>
                                {doc.original_name}
                                {doc.isPending ? ' (pending save)' : ''}
                            </span>
                            <span className="grn-upload-centre-actions">
                                <button
                                    type="button"
                                    title="View"
                                    onClick={() => openGrnDocument(doc)}
                                    disabled={!doc.fileUrl}
                                >
                                    <VisibilityIcon sx={{ color: '#1565c0', fontSize: 22, cursor: 'pointer' }} />
                                </button>
                                <button
                                    type="button"
                                    title="Remove"
                                    onClick={() => handleRemove(doc)}
                                    disabled={busy}
                                >
                                    <DeleteOutlineIcon sx={{ color: '#c62828', fontSize: 22, cursor: 'pointer' }} />
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            </ModalBody>
            <ModalFooter className="justify-content-center">
                <Button color="secondary" type="button" onClick={toggle} disabled={busy}>Close</Button>
            </ModalFooter>
        </Modal>
    );
}

export default PiUploadCentreModal;
